/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { communicationAttachmentFromFile, EDoc } from '@gh/builder-shared';
import { EDocSigningStatus, EDocType } from '@gh/builder-shared-data';
import { dictionaryValueFormatter, FilePicker, ROW_DELETED_CLASS } from '@gh/common-internal-shared';
import { DateTimeTypes, FormState, FormStateBuilder, getRouteInitData } from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import { FormMode, getControlValue, STYLES_FLEX_COLUMN_LAYOUT } from '@gh/core-ui';
import { buildDateTimeColumn, GridDefinition, GridDefinitionFactory } from '@gh/core-ui-grid';

import { isEmpty, sortBy, values } from 'lodash';

import { JobEDocDocumentSet, JobEDocLog, JobEDocSigningDocument } from '../../../../resources/job-details-tab';

@Component({
  selector: 'gh-job-details-e-docs-management',
  template: require('./job-details-e-docs-management.component.html'),
  styles: [STYLES_FLEX_COLUMN_LAYOUT],
})
export class JobDetailsEDocsManagementComponent implements OnInit {
  @Input() editable: boolean;
  @Input() log: JobEDocLog;
  @Input() logLoading: boolean;

  @Output() sign = new EventEmitter<JobEDocSigningDocument[]>();
  // tslint:disable-next-line:no-output-named-after-standard-event
  @Output() open = new EventEmitter<EDoc>();

  form: FormState<JobEDocDocumentSet>;
  gridDefinition: GridDefinition;
  contractPicker: FilePicker;
  specificationPicker: FilePicker;
  plansPicker: FilePicker;
  authorityToCommencePicker: FilePicker;
  colorSelectionPicker: FilePicker;
  otherDocumentsPicker: FilePicker;

  constructor(private route: ActivatedRoute,
              private gridDefinitionFactory: GridDefinitionFactory,
              private formStateBuilder: FormStateBuilder,
              private translations: TranslationService) {
  }

  get canSendContractDocs(): boolean {
    const contract = getControlValue(this.form.group, 'contract');
    const specification = getControlValue(this.form.group, 'specification');
    const plans = getControlValue(this.form.group, 'plans');

    return !isEmpty(contract) && !isEmpty(specification) && !isEmpty(plans);
  }

  get canSendAuthorityToCommence(): boolean {
    const otherDocuments = getControlValue(this.form.group, 'authorityToCommence');

    return !isEmpty(otherDocuments);
  }

  get canSendColorSelection(): boolean {
    const colorSelection = getControlValue(this.form.group, 'colorSelection');

    return !isEmpty(colorSelection);
  }

  get canSendOtherDocuments(): boolean {
    const otherDocuments = getControlValue(this.form.group, 'otherDocuments');

    return !isEmpty(otherDocuments);
  }

  ngOnInit(): void {
    const { signingStatusDict } = getRouteInitData(this.route);

    this.gridDefinition = this.gridDefinitionFactory.buildManual(
      [
        {
          field: 'addedDate',
          headerName: this.translations.get('lbl_added_date'),
          width: 180,
          suppressSizeToFit: true,
          ...buildDateTimeColumn(DateTimeTypes.DateTime),
        },
        { field: 'sender', headerName: this.translations.get('lbl_sender'), width: 150, suppressSizeToFit: true },
        {
          field: 'files',
          headerName: this.translations.get('lbl_documents'),
          width: 150,
          valueFormatter: ({ value }) => sortBy(values(value)).join(', '),
          sortable: false,
        },
        {
          field: 'status',
          headerName: this.translations.get('lbl_eversign_status'),
          width: 150,
          suppressSizeToFit: true,
          valueFormatter: dictionaryValueFormatter(signingStatusDict),
          cellClass: ({ data }: { data: any }) => ({
            [EDocSigningStatus.Signed]: 'cell--positive',
            [EDocSigningStatus.Cancelled]: 'cell--important',
            [EDocSigningStatus.Failed]: 'cell--negative',
          }[data.status]),
        },
      ],
      {
        rowClassRules: {
          [ROW_DELETED_CLASS]: ({ data }: { data: EDoc }) => data.status === EDocSigningStatus.Failed,
        },
        getRowNodeId: (data: EDoc) => data.id,
      });

    this.form = this.formStateBuilder.buildPlain({
      mode: this.editable ? FormMode.EditMode : FormMode.ViewMode,
      children: {
        contract: void 0,
        specification: void 0,
        plans: void 0,
        authorityToCommence: void 0,
        colorSelection: void 0,
        otherDocuments: void 0,
      },
    });
  }

  onSendContractDocs(): void {
    this.sendToSign(['contract', 'specification', 'plans']);
  }

  onSendAuthorityToCommence(): void {
    this.sendToSign(['authorityToCommence']);
  }

  onSendColorSelection(): void {
    this.sendToSign(['colorSelection']);
  }

  onSendOtherDocuments(): void {
    this.sendToSign(['otherDocuments']);
  }

  onOpen(document: EDoc): void {
    this.open.emit(document);
  }

  private sendToSign(fields: string[]): void {
    // @ts-ignore
    this.sign.emit(fields.map((field) => ({
      type: {
        'contract': EDocType.Contract,
        'specification': EDocType.JobSpec,
        'plans': EDocType.Plans,
        'authorityToCommence': EDocType.AuthToCommence,
        'colorSelection': EDocType.ColorSelection,
        'otherDocuments': EDocType.Other,
      }[field],
      files: getControlValue(this.form.group, field).map(communicationAttachmentFromFile),
    })));
  }
}
